import { subscribe } from '@nextcloud/event-bus';
import { FileListFilter, registerFileListFilter } from '@nextcloud/files';
import { ShareType } from '@nextcloud/sharing';
import Vue from 'vue';
import FileListFilterAccount from '../components/FileListFilterAccount.vue';
import { isPublicShare } from '@nextcloud/sharing/public';
import { TRASHBIN_VIEW_ID } from '../../../files_trashbin/src/files_views/trashbinView';
/**
 * File list filter to filter by owner / sharee
 */
class AccountFilter extends FileListFilter {
    availableAccounts;
    currentInstance;
    filterAccounts;
    constructor() {
        super('files_sharing:account', 100);
        this.availableAccounts = [];
        subscribe('files:list:updated', ({ contents }) => {
            this.updateAvailableAccounts(contents);
        });
    }
    mount(el) {
        if (this.currentInstance) {
            this.currentInstance.$destroy();
        }
        const View = Vue.extend(FileListFilterAccount);
        this.currentInstance = new View({ el })
            .$on('update:accounts', (accounts) => this.setAccounts(accounts))
            .$mount();
        this.currentInstance
            .setAvailableAccounts(this.availableAccounts);
    }
    filter(nodes) {
        if (!this.filterAccounts || this.filterAccounts.length === 0) {
            return nodes;
        }
        const userIds = this.filterAccounts.map(({ uid }) => uid);
        // Filter if the owner of the node is in the list of filtered accounts
        return nodes.filter((node) => {
            if (window.OCP.Files.Router.params.view === TRASHBIN_VIEW_ID) {
                const deletedBy = node.attributes?.['trashbin-deleted-by-id'];
                if (deletedBy && userIds.includes(deletedBy)) {
                    return true;
                }
                return false;
            }
            // if the owner matches
            if (node.owner && userIds.includes(node.owner)) {
                return true;
            }
            // Or any of the sharees (if only one share this will be an object, otherwise an array. So using `.flat()` to make it always an array)
            const sharees = node.attributes.sharees?.sharee;
            if (sharees && [sharees].flat().some(({ id }) => userIds.includes(id))) {
                return true;
            }
            // If the node provides no information lets keep it
            if (!node.owner && !sharees) {
                return true;
            }
            // Not a valid node for the current filter
            return false;
        });
    }
    reset() {
        this.currentInstance?.resetFilter();
    }
    /**
     * Set accounts that should be filtered.
     *
     * @param accounts - Account to filter or undefined if inactive.
     */
    setAccounts(accounts) {
        this.filterAccounts = accounts;
        let chips = [];
        if (this.filterAccounts && this.filterAccounts.length > 0) {
            chips = this.filterAccounts.map(({ displayName, uid }) => ({
                text: displayName,
                user: uid,
                onclick: () => this.currentInstance?.toggleAccount(uid),
            }));
        }
        this.updateChips(chips);
        this.filterUpdated();
    }
    /**
     * Update the accounts owning nodes or have nodes shared to them.
     *
     * @param nodes - The current content of the file list.
     */
    updateAvailableAccounts(nodes) {
        const available = new Map();
        for (const node of nodes) {
            const owner = node.owner;
            if (owner && !available.has(owner)) {
                available.set(owner, {
                    uid: owner,
                    displayName: node.attributes['owner-display-name'] ?? node.owner,
                });
            }
            // ensure sharees is an array (if only one share then it is just an object)
            const sharees = [node.attributes.sharees?.sharee].flat().filter(Boolean);
            for (const sharee of [sharees].flat()) {
                // Skip link shares and other without user
                if (sharee.id === '') {
                    continue;
                }
                if (sharee.type !== ShareType.User && sharee.type !== ShareType.Remote) {
                    continue;
                }
                // Add if not already added
                if (!available.has(sharee.id)) {
                    available.set(sharee.id, {
                        uid: sharee.id,
                        displayName: sharee['display-name'],
                    });
                }
            }
            // lets also handle trashbin
            const deletedBy = node.attributes?.['trashbin-deleted-by-id'];
            if (deletedBy) {
                available.set(deletedBy, {
                    uid: deletedBy,
                    displayName: node.attributes?.['trashbin-deleted-by-display-name'] || deletedBy,
                });
            }
        }
        this.availableAccounts = [...available.values()];
        if (this.currentInstance) {
            this.currentInstance.setAvailableAccounts(this.availableAccounts);
        }
    }
}
/**
 * Register the file list filter by owner or sharees
 */
export function registerAccountFilter() {
    if (isPublicShare()) {
        // We do not show the filter on public pages - it makes no sense
        return;
    }
    registerFileListFilter(new AccountFilter());
}
